import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Calendar, ArrowLeftRight, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import {
  timeOffService,
  PTOAllocation,
  TimeOffRequest,
  ShiftSwapRequest,
  RequestStatus,
} from '../../services/time-off.service';
import PTOAllocationCard from '../../components/time-off/PTOAllocationCard';
import RequestTimeOffModal from '../../components/time-off/RequestTimeOffModal';
import RequestShiftSwapModal from '../../components/time-off/RequestShiftSwapModal';

export default function TimeOffPage() {
  const user = useAuthStore((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [allocation, setAllocation] = useState<PTOAllocation | null>(null);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
  const [shiftSwapRequests, setShiftSwapRequests] = useState<ShiftSwapRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Get tab directly from URL, no local state needed for the actual tab value
  const urlTab = searchParams.get('tab');
  const activeTab = (urlTab === 'shift-swaps' ? 'shift-swaps' : 'time-off') as 'time-off' | 'shift-swaps';

  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [showShiftSwapModal, setShowShiftSwapModal] = useState(false);

  useEffect(() => {
    if (!hasLoadedOnce) {
      loadData();
    }
  }, [user?.id, hasLoadedOnce]);

  // Handler to change tabs - directly updates URL, activeTab derives from URL
  const handleTabChange = (tab: 'time-off' | 'shift-swaps') => {
    setSearchParams({ tab }, { replace: true });
  };

  const loadData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const [allocationData, timeOffData, shiftSwapData] = await Promise.all([
        timeOffService.getAllocation(user.id).catch(() => getDemoAllocation()),
        timeOffService.getMyTimeOffRequests(user.id).catch(() => getDemoTimeOffRequests()),
        timeOffService.getMyShiftSwapRequests(user.id).catch(() => getDemoShiftSwapRequests()),
      ]);

      setAllocation(allocationData);
      setTimeOffRequests(timeOffData);
      setShiftSwapRequests(shiftSwapData);
      setHasLoadedOnce(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getDemoAllocation = (): PTOAllocation => {
    return {
      id: 'pto-001',
      agentId: user?.id || '',
      agentName: user?.firstName + ' ' + user?.lastName,
      year: 2026,
      vacationDays: 15,
      sickDays: 10,
      personalDays: 5,
      bereavementDays: 3,
      vacationUsed: 3,
      sickUsed: 1,
      personalUsed: 0,
      bereavementUsed: 0,
      vacationRemaining: 12,
      sickRemaining: 9,
      personalRemaining: 5,
      bereavementRemaining: 3,
      totalAllocated: 33,
      totalUsed: 4,
      totalRemaining: 29,
      createdAt: '2026-01-01T00:00:00Z',
      updatedAt: '2026-01-09T00:00:00Z',
    };
  };

  const getDemoTimeOffRequests = (): TimeOffRequest[] => {
    return [
      {
        id: 'req-001',
        agentId: user?.id || '',
        agentName: user?.firstName + ' ' + user?.lastName,
        type: 'VACATION',
        startDate: '2026-02-10',
        endDate: '2026-02-14',
        totalDays: 5,
        reason: 'Family vacation',
        status: 'PENDING',
        requestedAt: '2026-01-08T10:30:00Z',
      },
      {
        id: 'req-002',
        agentId: user?.id || '',
        agentName: user?.firstName + ' ' + user?.lastName,
        type: 'SICK',
        startDate: '2025-12-15',
        endDate: '2025-12-15',
        totalDays: 1,
        status: 'APPROVED',
        requestedAt: '2025-12-15T08:00:00Z',
        reviewedAt: '2025-12-15T09:00:00Z',
        reviewerName: 'Manager Smith',
        reviewNotes: 'Approved',
      },
      {
        id: 'req-003',
        agentId: user?.id || '',
        agentName: user?.firstName + ' ' + user?.lastName,
        type: 'VACATION',
        startDate: '2025-12-24',
        endDate: '2025-12-26',
        totalDays: 3,
        status: 'APPROVED',
        requestedAt: '2025-11-20T14:00:00Z',
        reviewedAt: '2025-11-21T10:00:00Z',
        reviewerName: 'Manager Smith',
      },
    ];
  };

  const getDemoShiftSwapRequests = (): ShiftSwapRequest[] => {
    return [
      {
        id: 'swap-001',
        requestingAgentId: user?.id || '',
        requestingAgentName: user?.firstName + ' ' + user?.lastName,
        targetAgentId: 'agent-002',
        targetAgentName: 'Marcus Johnson',
        requestingShiftId: 'shift-001',
        targetShiftId: 'shift-101',
        requestingShiftDate: '2026-01-15',
        targetShiftDate: '2026-01-14',
        requestingShiftTime: '09:00-17:00',
        targetShiftTime: '09:00-17:00',
        status: 'PENDING',
        requestedAt: '2026-01-09T09:00:00Z',
        targetAgentApproved: false,
      },
    ];
  };

  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case 'APPROVED':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'DENIED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'CANCELLED':
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'DENIED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Only show loading spinner on initial load, not on tab switches
  if (!hasLoadedOnce && (isLoading || !allocation)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no allocation after initial load, still show something
  if (!allocation) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No allocation data available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Time Off & Shift Swaps</h1>
        <p className="text-gray-600 mt-2">Manage your time off and schedule changes</p>
      </div>

      {/* PTO Allocation Card */}
      <PTOAllocationCard allocation={allocation} />

      {/* Action Buttons */}
      <div className="flex items-center space-x-3">
        <button
          onClick={() => setShowTimeOffModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Request Time Off</span>
        </button>
        <button
          onClick={() => setShowShiftSwapModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <ArrowLeftRight className="h-5 w-5" />
          <span>Request Shift Swap</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => handleTabChange('time-off')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'time-off'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Time Off Requests</span>
              <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs">
                {timeOffRequests.filter((r) => r.status === 'PENDING').length}
              </span>
            </div>
          </button>
          <button
            onClick={() => handleTabChange('shift-swaps')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'shift-swaps'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <ArrowLeftRight className="h-4 w-4" />
              <span>Shift Swap Requests</span>
              <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                {shiftSwapRequests.filter((r) => r.status === 'PENDING').length}
              </span>
            </div>
          </button>
        </nav>
      </div>

      {/* Time Off Requests */}
      {activeTab === 'time-off' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {timeOffRequests.length === 0 ? (
            <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No time off requests</h3>
            <p className="text-gray-600">
              You haven't submitted any time off requests yet
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {timeOffRequests.map((request) => (
                <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded">
                          {request.type}
                        </span>
                        <span
                          className={`flex items-center space-x-1 px-3 py-1 text-sm font-medium rounded border ${getStatusColor(
                            request.status
                          )}`}
                        >
                          {getStatusIcon(request.status)}
                          <span>{request.status}</span>
                        </span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-900 font-medium">
                          {new Date(request.startDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}{' '}
                          -{' '}
                          {new Date(request.endDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                          <span className="text-gray-600 ml-2">
                            ({request.totalDays} day{request.totalDays !== 1 ? 's' : ''})
                          </span>
                        </p>
                        {request.reason && (
                          <p className="text-gray-600">Reason: {request.reason}</p>
                        )}
                        <p className="text-gray-500">
                          Requested:{' '}
                          {new Date(request.requestedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                        {request.reviewedAt && (
                          <p className="text-gray-500">
                            Reviewed by {request.reviewerName} on{' '}
                            {new Date(request.reviewedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                        {request.reviewNotes && (
                          <p className="text-gray-600 italic">Note: {request.reviewNotes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Shift Swap Requests */}
      {activeTab === 'shift-swaps' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {shiftSwapRequests.length === 0 ? (
            <div className="p-12 text-center">
            <ArrowLeftRight className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shift swap requests</h3>
            <p className="text-gray-600">
              You haven't submitted any shift swap requests yet
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {shiftSwapRequests.map((request) => (
                <div key={request.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <span
                      className={`flex items-center space-x-1 px-3 py-1 text-sm font-medium rounded border ${getStatusColor(
                        request.status
                      )}`}
                    >
                      {getStatusIcon(request.status)}
                      <span>{request.status}</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-red-900 font-medium mb-1">You give:</p>
                      <p className="text-gray-900">
                        {new Date(request.requestingShiftDate).toLocaleDateString()}
                      </p>
                      <p className="text-gray-600">{request.requestingShiftTime}</p>
                    </div>
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <p className="text-green-900 font-medium mb-1">You receive:</p>
                      <p className="text-gray-900">
                        {new Date(request.targetShiftDate).toLocaleDateString()}
                      </p>
                      <p className="text-gray-600">{request.targetShiftTime}</p>
                    </div>
                  </div>
                  <div className="mt-3 text-sm text-gray-600">
                    <p>
                      Swap with: <span className="font-medium">{request.targetAgentName}</span>
                    </p>
                    <p className="text-gray-500">
                      Requested:{' '}
                      {new Date(request.requestedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                    {request.status === 'PENDING' && !request.targetAgentApproved && (
                      <p className="text-yellow-600 mt-1">
                        Waiting for {request.targetAgentName} to accept
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showTimeOffModal && user && (
        <RequestTimeOffModal
          agentId={user.id}
          onClose={() => setShowTimeOffModal(false)}
          onSuccess={() => {
            setShowTimeOffModal(false);
            loadData();
          }}
        />
      )}

      {showShiftSwapModal && user && (
        <RequestShiftSwapModal
          agentId={user.id}
          onClose={() => setShowShiftSwapModal(false)}
          onSuccess={() => {
            setShowShiftSwapModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}
